import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import './css/AdminPanel.css';
import { changeOrderStage, getAllUsers } from '../../Utils/RestUtils';
import {
  getOrderStage,
  getTouristsText,
  getNightsTextNoRange,
} from '../../Utils/CommonUtils';
import {
  SUCCESS_STATUS,
  ORDER_WAIT_APPROVAL_STATUS,
  ORDER_APPROVED_STATUS,
} from '../../Utils/Constants/GlobalConstants';
import {
  ORDER_STAGES,
  STAGE_FUNDS_HOLD,
  STAGE_APPROVED,
  NO_ORDERS_IN_STAGE,
  NEXT_STAGE,
  NEXT_STAGE_FUNDS_HOLD,
  NEXT_STAGE_APPROVED,
  STAGE_EMAIL_CONFIRM,
  STAGE_CONFIRM_YES,
  STAGE_CONFIRM_NO,
  BOARD_TAB,
  ARCHIVE_TAB,
  ARCHIVE_SEARCH_PLACEHOLDER,
  ARCHIVE_NOT_FOUND,
  NO_ARCHIVED_ORDERS,
} from '../../Utils/Constants/AdminConstants';
import AdminOrderModal from './AdminOrderModal/AdminOrderModal';
import AdminCreateOrderModal from './AdminCreateOrderModal/AdminCreateOrderModal';
import OrderStatusBadge from './OrderStatusBadge/OrderStatusBadge';
import { ReactComponent as Person } from '../../images/gray-person-r.svg';
import { ReactComponent as Arrow } from '../../images/gray-arrow-right-r.svg';

const getStageIndex = (order) =>
  ORDER_STAGES.findIndex((stage) => stage.id === getOrderStage(order));

// moving into these stages emails the customer: the arrow hint, then a confirm
const STAGE_EMAIL_HINTS = {
  [STAGE_FUNDS_HOLD]: NEXT_STAGE_FUNDS_HOLD,
  [STAGE_APPROVED]: NEXT_STAGE_APPROVED,
};

// the backend sets these statuses together with the stage
const STAGE_STATUSES = {
  [STAGE_FUNDS_HOLD]: ORDER_WAIT_APPROVAL_STATUS,
  [STAGE_APPROVED]: ORDER_APPROVED_STATUS,
};

const getNextStage = (stageIndex) => ORDER_STAGES[stageIndex + 1];

const getBuyerName = (order) => {
  const { contactInfo } = order;

  if (!contactInfo) {
    return '';
  }

  return [contactInfo.lastName, contactInfo.firstName]
    .filter((item) => item)
    .join(' ');
};

function Orders(props) {
  const { isCreateModalVisible, closeCreateModal } = props;
  const [orders, setOrders] = useState([]);
  const [isArchiveVisible, setIsArchiveVisible] = useState(false);
  const [archiveSearch, setArchiveSearch] = useState('');
  // the order waiting for the move confirmation: the move emails the customer
  const [confirmOrderId, setConfirmOrderId] = useState(null);
  const [pendingOrderId, setPendingOrderId] = useState(null);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [isOrderModalVisible, setIsOrderModalVisible] = useState(false);
  const orderModalNode = useRef(null);
  const [users, setUsers] = useState([]);
  const createModalNode = useRef(null);

  const getOrders = () => {
    const token = window.localStorage.getItem('accessToken');

    axios
      .get('/api/order/all', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((result) => {
        setOrders(result.data);
      })
      .catch(() => {});
  };

  const getUsers = () => {
    const token = window.localStorage.getItem('accessToken');

    getAllUsers(token)
      .then((response) => {
        if (response && response.data) {
          setUsers(response.data);
        }
      })
      .catch(() => {});
  };

  useEffect(() => {
    getOrders();
    getUsers();
  }, []);

  const getOrderUser = (order) =>
    users.find((user) => user.id === order.userId) || null;

  const closeOrderModal = useCallback(() => {
    setIsOrderModalVisible(false);
  }, []);

  const openOrderModal = (orderId) => {
    setSelectedOrderId(orderId);
    setIsOrderModalVisible(true);
  };

  useEffect(() => {
    if (!isOrderModalVisible) {
      return undefined;
    }

    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    document.body.style.paddingRight = `${scrollbarWidth}px`;

    // the opening click reaches document after this effect runs (React 18);
    // a clicked button may be already removed by the re-render (edit -> form)
    const handleOutsideClick = (e) => {
      const node = orderModalNode.current;

      if (
        !node ||
        !e.target.isConnected ||
        node.contains(e.target) ||
        e.target.closest('.ap_board_card_link')
      ) {
        return;
      }

      closeOrderModal();
    };

    document.addEventListener('click', handleOutsideClick, false);

    return () => {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      document.removeEventListener('click', handleOutsideClick, false);
    };
  }, [isOrderModalVisible, closeOrderModal]);

  useEffect(() => {
    if (!isCreateModalVisible) {
      return undefined;
    }

    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    document.body.style.paddingRight = `${scrollbarWidth}px`;

    const handleOutsideClick = (e) => {
      const node = createModalNode.current;

      if (
        !node ||
        !e.target.isConnected ||
        node.contains(e.target) ||
        e.target.closest('.ap_board_create')
      ) {
        return;
      }

      closeCreateModal();
    };

    document.addEventListener('click', handleOutsideClick, false);

    return () => {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      document.removeEventListener('click', handleOutsideClick, false);
    };
  }, [isCreateModalVisible, closeCreateModal]);

  const handleOrderCreated = () => {
    closeCreateModal();
    getOrders();
    // the order may belong to a just created user
    getUsers();
  };

  const updateOrder = (orderId, changes) => {
    const update = (item) => {
      if (item.id === orderId) {
        return { ...item, ...changes };
      }

      return item;
    };

    setOrders((prevOrders) => prevOrders.map(update));
  };

  const removeOrderFromBoard = (orderId) => {
    closeOrderModal();
    setOrders((prevOrders) => prevOrders.filter((item) => item.id !== orderId));
  };

  // stages only move forward
  const moveOrder = (order) => {
    const nextStage = getNextStage(getStageIndex(order));
    const token = window.localStorage.getItem('accessToken');

    if (!nextStage) {
      return;
    }

    setPendingOrderId(order.id);

    changeOrderStage(order.id, nextStage.id, token)
      .then((response) => {
        if (response && response.data === SUCCESS_STATUS) {
          const status = STAGE_STATUSES[nextStage.id];
          const changes = status
            ? { stage: nextStage.id, status }
            : { stage: nextStage.id };

          updateOrder(order.id, changes);
        }
      })
      .catch(() => {})
      .finally(() => {
        setPendingOrderId(null);
      });
  };

  const renderBuyerText = (order, orderUser) => {
    const name = getBuyerName(order);
    const email = (orderUser && orderUser.email) || order.email;

    return (
      <>
        <Person />
        <span className="ap_board_buyer_text">
          {name && (
            <span className="ap_board_buyer_name br_text_14">{name}</span>
          )}
          {email && (
            <span className="ap_board_buyer_email gr_text_13">{email}</span>
          )}
        </span>
      </>
    );
  };

  const renderBuyer = (order, orderUser) => {
    if (!getBuyerName(order) && !(orderUser && orderUser.email) && !order.email) {
      return null;
    }

    return (
      <div className="ap_board_buyer">{renderBuyerText(order, orderUser)}</div>
    );
  };

  const getEmailHint = (stageIndex) => {
    const nextStage = getNextStage(stageIndex);
    return nextStage ? STAGE_EMAIL_HINTS[nextStage.id] : null;
  };

  const handleMoveClick = (order, stageIndex) => {
    if (getEmailHint(stageIndex)) {
      setConfirmOrderId(order.id);
      return;
    }

    moveOrder(order);
  };

  const renderMoveConfirm = (order) => (
    <div className="ap_board_confirm">
      <div className="gr_text_13">{STAGE_EMAIL_CONFIRM}</div>
      <div className="ap_board_confirm_buttons">
        <button
          type="button"
          className="ap_board_confirm_btn light_gray_button bm_text_14"
          onClick={() => setConfirmOrderId(null)}
        >
          {STAGE_CONFIRM_NO}
        </button>
        <button
          type="button"
          className="ap_board_confirm_btn btn__yellow bm_text_14"
          disabled={pendingOrderId === order.id}
          onClick={() => {
            setConfirmOrderId(null);
            moveOrder(order);
          }}
        >
          {STAGE_CONFIRM_YES}
        </button>
      </div>
    </div>
  );

  // archived cards have no stage arrows, the stage is shown as a badge
  const renderOrderCard = (order, stageIndex, isArchived = false) => {
    const isPending = pendingOrderId === order.id;
    const nights = parseInt(order.nightsCount, 10);
    const orderUser = getOrderUser(order);

    return (
      <div key={`ap-board-order-${order.id}`} className="ap_board_card">
        <button
          type="button"
          className="ap_board_card_link"
          onClick={() => openOrderModal(order.id)}
        >
          <div className="ap_board_card_head">
            <div className="dbm_text_16">{`№${order.externalId}`}</div>
            <div className="bm_text_16">
              {`${order.originalPrice} ${order.currency}`}
            </div>
          </div>
          {renderBuyer(order, orderUser)}
          <div className="bm_text_14 mb_5">
            {`${order.hotelName} ${order.hotelCategory}*`}
          </div>
          <div className="gr_text_14 mb_5">
            {`${order.countryTo}, ${order.resort}`}
          </div>
          <div className="gr_text_14 mb_5">
            {`${order.departureDate}, ${getNightsTextNoRange(nights)}`}
          </div>
          <div className="gr_text_14">
            {getTouristsText(order.adultsCount, parseInt(order.kidsCount, 10))}
          </div>
          {/* an archived order needs neither status nor stage */}
          {!isArchived && (
            <div className="ap_board_card_status">
              <OrderStatusBadge status={order.status} />
            </div>
          )}
        </button>
        {!isArchived && (
          <div className="ap_board_card_actions">
            {confirmOrderId === order.id ? (
              renderMoveConfirm(order)
            ) : (
              <button
                type="button"
                className="ap_board_move"
                title={getEmailHint(stageIndex) || NEXT_STAGE}
                disabled={isPending || stageIndex === ORDER_STAGES.length - 1}
                onClick={() => handleMoveClick(order, stageIndex)}
              >
                <Arrow />
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  const boardOrders = orders.filter((order) => !order.archived);
  const archivedOrders = orders.filter((order) => order.archived);

  const renderStage = (stage, stageIndex) => {
    const stageOrders = boardOrders.filter(
      (order) => getStageIndex(order) === stageIndex,
    );

    return (
      <div key={`ap-board-stage-${stage.id}`} className="ap_board_column">
        <div
          className="ap_board_stage"
          style={{ backgroundColor: stage.color }}
        >
          <span className="bm_text_14">{stage.title}</span>
          <span className="ap_board_count">{stageOrders.length}</span>
        </div>
        <div className="ap_board_cards">
          {stageOrders.length > 0 ? (
            stageOrders.map((order) => renderOrderCard(order, stageIndex))
          ) : (
            <div className="ap_board_empty gr_text_14">
              {NO_ORDERS_IN_STAGE}
            </div>
          )}
        </div>
      </div>
    );
  };

  const selectedOrder = orders.find((order) => order.id === selectedOrderId);

  // by the order number the customer sees (externalId) or the db id
  const searchValue = archiveSearch.trim().replace('№', '').toLowerCase();
  const foundArchivedOrders = archivedOrders.filter(
    (order) =>
      !searchValue ||
      `${order.externalId}`.toLowerCase().includes(searchValue) ||
      `${order.id}`.toLowerCase().includes(searchValue),
  );

  const renderArchiveOrders = () => {
    if (archivedOrders.length === 0) {
      return (
        <div className="ap_board_empty gr_text_14">{NO_ARCHIVED_ORDERS}</div>
      );
    }

    if (foundArchivedOrders.length === 0) {
      return <div className="ap_board_empty gr_text_14">{ARCHIVE_NOT_FOUND}</div>;
    }

    return (
      <div className="ap_archive_cards">
        {foundArchivedOrders.map((order) =>
          renderOrderCard(order, getStageIndex(order), true))}
      </div>
    );
  };

  const renderArchive = () => (
    <>
      <input
        className="ap_text_input ap_archive_search"
        type="text"
        value={archiveSearch}
        placeholder={ARCHIVE_SEARCH_PLACEHOLDER}
        onChange={(e) => setArchiveSearch(e.target.value)}
      />
      {renderArchiveOrders()}
    </>
  );

  const getTabClass = (isArchiveTab) =>
    `ap_board_tab bm_text_14 ${
      isArchiveVisible === isArchiveTab ? 'ap_board_tab_selected' : ''
    }`;

  return (
    <div>
      <div className="ap_board_tabs">
        <button
          type="button"
          className={getTabClass(false)}
          onClick={() => setIsArchiveVisible(false)}
        >
          {BOARD_TAB}
          <span className="ap_board_count">{boardOrders.length}</span>
        </button>
        <button
          type="button"
          className={getTabClass(true)}
          onClick={() => setIsArchiveVisible(true)}
        >
          {ARCHIVE_TAB}
          <span className="ap_board_count">{archivedOrders.length}</span>
        </button>
      </div>
      {isArchiveVisible ? (
        renderArchive()
      ) : (
        <div className="ap_board">{ORDER_STAGES.map(renderStage)}</div>
      )}
      <AdminOrderModal
        isVisible={isOrderModalVisible}
        modalNode={orderModalNode}
        handleModalClick={closeOrderModal}
        order={selectedOrder || null}
        handleOrderUpdate={updateOrder}
        handleOrderDelete={removeOrderFromBoard}
      />
      <AdminCreateOrderModal
        isVisible={isCreateModalVisible}
        modalNode={createModalNode}
        handleModalClick={closeCreateModal}
        handleOrderCreated={handleOrderCreated}
      />
    </div>
  );
}

Orders.propTypes = {
  isCreateModalVisible: PropTypes.bool.isRequired,
  closeCreateModal: PropTypes.func.isRequired,
};

export default Orders;
